/**
 * Объявление ленты грузов и подача offer'а — блок `WEB-2.2`, требования 402,
 * 403 и 404.
 *
 * Экран читает объявление (`getCargoListing`) и профиль (`getCurrentUser`) и
 * показывает объявление целиком при любом праве смотрящего: маршрут, даты,
 * свободные машины, груз, кузов подписью, ADR, км и цену суммой и валютой
 * ответа рядом с подписью торга (CR-003 §1.1 — цену объявления видит любой
 * вошедший).
 *
 * Условие подачи — **в одном месте**, здесь (решение формы Б3): форма есть
 * ровно тогда, когда у членства фирмы из адреса `can_take_cargo` и эта фирма —
 * в `offer_companies` объявления. Оба признака считает сервер; роль, статус
 * объявления, проверенность фирмы и свободные машины формы не решают — у
 * пустого списка фирм шесть причин, и все они сервера. Без права — строка
 * «право выдаёт директор»; с правом, но без фирмы в списке — статус своего
 * offer'а с фирмой и ссылкой «Мои предложения» либо нейтральная строка без
 * причины (решение формы Б9).
 *
 * Форма отправляет **ровно** поля `CargoOfferInput`: фирму из адреса, число
 * машин, сумму своей цены строкой как введена либо `null` при согласии на
 * условия, комментарий либо `null`. «Своя цена» и поле суммы есть только при
 * `negotiable` объявления; валюта — объявления, поля валюты в теле нет. Число
 * машин ни с чем не сравнивается, порог цены и «поздно» не вычисляются —
 * отвечает `422` сервер, и его сообщение ложится под своим полем. Повтор
 * отправки до ответа — тот же `Idempotency-Key`, новый — после ответа сервера
 * или правки поля (решение формы Б10); `201` — переход на «Мои предложения».
 *
 * Смена языка не стирает введённое (долг `TD-72`): экран переподписывается, а
 * не пересобирается.
 */

import { ApiFailure, newIdempotencyKey } from '../api/client';
import type { ApiClient, CargoOfferSubmission } from '../api/client';
import { membershipsIn } from '../api/contract';
import type { CargoListing, CargoOffer, Membership } from '../api/contract';
import { activeLocale, bodyTypeStringKey, cargoOfferStatusStringKey, paymentMethodStringKey, paymentMomentStringKey } from '../i18n';
import type { Strings } from '../i18n';
import { cargoFeedPath, cargoOffersPath, navigate } from '../router';
import {
  createFieldMessageSlot,
  dateLabel,
  enumLabel,
  moneyLabel,
  placeState,
  refusalText,
  renderFieldMessages,
  screenStateFor,
} from './state';
import type { ScreenState } from './state';

export interface CargoFeedListingScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
  readonly companyId: string;
  readonly listingId: string;
}

export interface CargoFeedListingScreen {
  readonly element: HTMLElement;
  /** Состояние экрана либо `null` — содержимое показано. */
  readonly state: ScreenState | null;
  load(): Promise<void>;
  /** Подписи на новом языке; введённое остаётся на месте (долг `TD-72`). */
  relabel(next: Strings): void;
}

/** Названа ли фирма из адреса среди фирм, от которых сервер примет offer. */
function offeredFrom(companies: CargoListing['offer_companies'], companyId: string): boolean {
  for (const company of companies) {
    if (company.id === companyId) {
      return true;
    }
  }

  return false;
}

/** Пустое поле — `null`; обязательность и пределы судит сервер. */
function textOf(raw: string): string | null {
  return raw === '' ? null : raw;
}

function integerOf(raw: string): number | null {
  return raw.trim() === '' ? null : Number(raw);
}

export function createCargoFeedListingScreen(options: CargoFeedListingScreenOptions): CargoFeedListingScreen {
  const { client, companyId, listingId } = options;
  let strings = options.strings;

  const element = document.createElement('section');
  const backLink = document.createElement('a');
  const heading = document.createElement('h1');
  const factsArea = document.createElement('div');
  const notice = document.createElement('p');
  const offerArea = document.createElement('div');
  const form = document.createElement('form');

  element.className = 'screen screen-cargo-feed-listing';
  backLink.className = 'company-link';
  backLink.href = cargoFeedPath(companyId);
  notice.className = 'notice';
  notice.setAttribute('role', 'alert');
  offerArea.className = 'cargo-feed-offer';
  form.className = 'form';
  form.noValidate = true;

  /** Подписи экрана: каждая ставится одной записью — и при сборке, и при смене языка. */
  const captions: Array<() => void> = [];

  function caption(apply: () => void): void {
    captions.push(apply);
    apply();
  }

  function labelled(control: HTMLElement, text: () => string, before = false): HTMLLabelElement {
    const label = document.createElement('label');
    const shown = document.createElement('span');

    label.className = 'form-field';
    shown.className = 'form-field__caption';
    caption(() => {
      shown.textContent = text();
    });

    if (before) {
      label.append(control, shown);
    } else {
      label.append(shown, control, createFieldMessageSlot());
    }

    return label;
  }

  function input(name: string, type: string): HTMLInputElement {
    const node = document.createElement('input');

    node.name = name;
    node.type = type;

    return node;
  }

  // ── Форма offer'а: узлы живут всё время экрана (долг `TD-72`) ───────────

  const agree = input('price_mode', 'radio');
  const ownPrice = input('price_mode', 'radio');
  const agreeLabel = labelled(agree, () => strings.cargo_feed_listing_agree, true);
  const ownPriceLabel = labelled(ownPrice, () => strings.cargo_feed_listing_own_price, true);
  const priceAmount = input('price_amount', 'text');
  const priceAmountLabel = labelled(priceAmount, () => strings.cargo_feed_listing_price_amount);
  const vehiclesCount = input('vehicles_count', 'number');
  const comment = document.createElement('textarea');
  const submitButton = document.createElement('button');

  agreeLabel.classList.add('form-field--check');
  ownPriceLabel.classList.add('form-field--check');
  // По умолчанию — согласие на условия объявления (решение формы Б10).
  agree.checked = true;
  priceAmount.inputMode = 'decimal';
  priceAmount.disabled = true;
  comment.name = 'comment';
  submitButton.type = 'submit';
  submitButton.className = 'action action--primary';

  caption(() => {
    submitButton.textContent = strings.cargo_feed_listing_submit;
    backLink.textContent = strings.cargo_feed_title;
  });

  const vehiclesLabel = labelled(vehiclesCount, () => strings.cargo_listing_form_vehicles_count);
  const commentLabel = labelled(comment, () => strings.cargo_listing_form_comment);

  function syncPriceMode(): void {
    priceAmount.disabled = !ownPrice.checked;
  }

  agree.addEventListener('change', syncPriceMode);
  ownPrice.addEventListener('change', syncPriceMode);

  /** Поля формы по торгу объявления: «Своя цена» и сумма — только при торге. */
  function buildForm(shown: CargoListing): void {
    const fields: HTMLElement[] = [agreeLabel];

    if (shown.negotiable) {
      fields.push(ownPriceLabel, priceAmountLabel);
    } else {
      ownPrice.checked = false;
      agree.checked = true;
    }

    fields.push(vehiclesLabel, commentLabel, submitButton);
    form.replaceChildren(...fields);
    syncPriceMode();
  }

  // ── Состояние экрана ────────────────────────────────────────────────────

  let state: ScreenState | null = 'loading';
  /** Пришёл ли ответ сервера — величина правила места состояния. */
  let answered = false;
  let listing: CargoListing | null = null;
  let membership: Membership | null = null;
  /** Запрос подачи в пути: повтор отправки до ответа не уходит вторым запросом. */
  let sending = false;
  /** Ключ идемпотентности текущей подачи либо `null`. */
  let idempotencyKey: string | null = null;

  function line(text: string): HTMLElement {
    const node = document.createElement('p');

    node.textContent = text;

    return node;
  }

  /** Поля объявления — как пришли; цена видна при любом праве (CR-003 §1.1). */
  function facts(shown: CargoListing): HTMLElement[] {
    const locale = activeLocale();
    const lines: HTMLElement[] = [
      line(
        [
          `${strings.cargo_listing_form_loading_at}: ${dateLabel(locale, shown.loading_at)}`,
          `${strings.cargo_listing_form_unload_by}: ${dateLabel(locale, shown.unload_by)}`,
        ].join(' · '),
      ),
    ];

    const route: string[] = [`${shown.distance_km} ${strings.cargo_listing_km}`];

    if (shown.transit_countries.length > 0) {
      route.push(`${strings.cargo_listing_transit}: ${shown.transit_countries.join(', ')}`);
    }

    lines.push(line(route.join(' · ')));
    lines.push(
      line(`${strings.cargo_listing_slots_free}: ${shown.slots_free} ${strings.cargo_listing_slots_of} ${shown.slots_total}`),
    );

    const cargo: string[] = [
      shown.cargo_type,
      `${shown.weight_kg} ${strings.cargo_listing_kg}`,
      `${strings.cargo_feed_body_type}: ${enumLabel(strings, bodyTypeStringKey(shown.body.type))}`,
    ];

    if (shown.adr) {
      cargo.push(strings.cargo_feed_adr);
    }

    lines.push(line(cargo.join(' · ')));

    const terms: string[] = [];

    if (shown.price !== null) {
      terms.push(`${strings.cargo_listing_form_price}: ${moneyLabel(strings, shown.price)}`);
    }

    terms.push(shown.negotiable ? strings.cargo_feed_listing_negotiable : strings.cargo_feed_listing_fixed_price);
    terms.push(
      `${strings.cargo_listing_payment}: ${enumLabel(strings, paymentMethodStringKey(shown.payment.method))}, ${enumLabel(strings, paymentMomentStringKey(shown.payment.moment))}`,
    );
    lines.push(line(terms.join(' · ')));

    if (shown.company_verified) {
      lines.push(line(strings.cargo_feed_company_verified));
    }

    if (shown.comment !== null) {
      lines.push(line(shown.comment));
    }

    // Свой offer фирмы — ответ сервера, и показывается он при любом праве, как
    // статус в строке ленты: это показ, а не условие подачи.
    const mine = shown.my_offer;

    if (mine !== null) {
      lines.push(...myOfferNodes(mine));
    }

    return lines;
  }

  /** Свой offer фирмы на это объявление — статусом, фирмой и путём к списку. */
  function myOfferNodes(offer: CargoOffer): HTMLElement[] {
    const link = document.createElement('a');

    link.className = 'company-link';
    link.href = cargoOffersPath(companyId);
    link.textContent = strings.cargo_offers_mine_title;

    return [
      line(`${strings.cargo_feed_my_offer}: ${enumLabel(strings, cargoOfferStatusStringKey(offer.status))} · ${offer.company.name}`),
      link,
    ];
  }

  /**
   * Что стоит на месте подачи — единственное место условия (решение формы Б3).
   * Решают два признака сервера: признак членства фирмы из адреса и список
   * фирм объявления. Иных полей объявления решение не читает. При своём
   * offer'е без фирмы в списке на месте подачи пусто — статус, фирма и путь к
   * «Моим предложениям» уже стоят в полях объявления (решение формы Б9).
   */
  function offerNodes(shown: CargoListing): HTMLElement[] {
    if (membership?.can_take_cargo !== true) {
      return [line(strings.cargo_feed_listing_no_right)];
    }

    if (offeredFrom(shown.offer_companies, companyId)) {
      return [form];
    }

    if (shown.my_offer !== null) {
      return [];
    }

    return [line(strings.cargo_feed_listing_unavailable)];
  }

  function render(): void {
    const frame: HTMLElement[] = [backLink];

    if (listing !== null) {
      heading.textContent = `${listing.origin} → ${listing.destination}`;
      factsArea.replaceChildren(...facts(listing));
      offerArea.replaceChildren(...offerNodes(listing));
      frame.push(heading, factsArea, notice);
    }

    placeState(element, { state, answered, frame, list: offerArea }, strings);
  }

  async function load(): Promise<void> {
    state = screenStateFor({ kind: 'pending' });
    answered = false;
    listing = null;
    membership = null;
    notice.textContent = '';
    render();

    try {
      const [received, profile] = await Promise.all([client.getCargoListing(listingId), client.getCurrentUser()]);

      listing = received;
      // Членство фирмы из адреса — из профиля, прочитанного сейчас: признак
      // сервер считает на каждый ответ, и кэша у клиента нет.
      membership = membershipsIn(profile.memberships, companyId)[0] ?? null;
      buildForm(received);
      answered = true;
      state = screenStateFor({ kind: 'done', empty: false });
    } catch (error) {
      listing = null;
      answered = false;
      state = screenStateFor({
        kind: 'failed',
        code: error instanceof ApiFailure ? error.code : null,
      });
    }

    render();
  }

  /** Тело — ровно поля `CargoOfferInput`; пустое поле уходит `null`. */
  function entered(): CargoOfferSubmission {
    return {
      company_id: companyId,
      vehicles_count: integerOf(vehiclesCount.value),
      // Своя цена уходит строкой как введена — и пустой тоже: «своя цена без
      // суммы» судит сервер, а `null` значил бы согласие, которого не давали.
      price_amount: ownPrice.checked ? priceAmount.value : null,
      comment: textOf(comment.value),
    };
  }

  async function send(): Promise<void> {
    sending = true;
    submitButton.disabled = true;
    notice.textContent = '';
    renderFieldMessages(form, []);

    try {
      idempotencyKey ??= newIdempotencyKey();
      await client.createCargoOffer(listingId, entered(), idempotencyKey);
      idempotencyKey = null;
      // Поданный offer открывается списком «Мои предложения»: его статус и
      // сводку груза список перечитывает у сервера, а не дописывает клиентом.
      navigate({ kind: 'cargoOffers', companyId });
    } catch (error) {
      const failure = error instanceof ApiFailure ? error : null;

      if (failure !== null && failure.code !== null) {
        // Сервер ответил — следующая отправка новая и несёт новый ключ. Обрыв
        // связи ответа не дал: сервер, возможно, offer уже завёл, и ключ остаётся.
        idempotencyKey = null;
      }

      // Форма остаётся со всем введённым: сообщения сервера — под полями,
      // которые он назвал; непоказанное и общий отказ — над формой.
      const homeless = renderFieldMessages(form, failure?.details ?? []);
      const texts: string[] = [];

      for (const detail of homeless) {
        texts.push(detail.message);
      }

      if (texts.length === 0) {
        texts.push(refusalText(error, strings));
      }

      notice.textContent = texts.join(' ');
    } finally {
      sending = false;
      submitButton.disabled = false;
    }
  }

  // Правка любого поля — новая отправка: ключ прежней снимается (решение Б10).
  form.addEventListener('input', () => {
    idempotencyKey = null;
  });
  form.addEventListener('change', () => {
    idempotencyKey = null;
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    if (sending) {
      return;
    }

    void send();
  });

  function relabel(next: Strings): void {
    strings = next;

    for (const apply of captions) {
      apply();
    }

    render();
  }

  render();

  return {
    element,

    get state(): ScreenState | null {
      return state;
    },

    load,
    relabel,
  };
}
